/* Activity Center (Phase 6) — ONE elegant floating component: live bidding
   activity + notifications + the AI concierge, in tabs. On public microsites
   the Activity tab pulses with that auction's real-time bids. */
import { Activity, ArrowUp, Loader2, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { ActivityFeed } from "@/components/ai/ActivityFeed";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/lib/activity";
import { OPEN_ACTIVITY_CENTER_EVENT } from "@/lib/activityCenter";
import { BACKEND_URL, TOKEN_KEY } from "@/lib/api";

const contextFor = (path) => {
  if (/^\/(dashboard|create)/.test(path)) return "organizer";
  if (/^\/e\/[^/]+\/(lots|live)/.test(path) || path.startsWith("/account/bids")) return "bidder";
  return "public";
};

export const ChatWidget = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("activity");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  /* launcher pulse: new live activity arriving while the panel is closed */
  const liveItems = useActivity();
  const seenRef = useRef(0);
  const hasNews = !open && liveItems.length > seenRef.current;
  useEffect(() => {
    if (open) seenRef.current = liveItems.length;
  }, [open, liveItems.length]);

  const context = contextFor(pathname);
  const eventSlug = (pathname.match(/^\/e\/([^/]+)/) || [])[1] || null;
  // Full-screen immersive surfaces keep their own focus.
  const hidden = /\/console$|\/builder$|^\/design-system/.test(pathname);

  const newSessionId = () =>
    (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const [sessionId, setSessionId] = useState("");
  useEffect(() => {
    const key = `aos_chat_${context}`;
    let sid = localStorage.getItem(key);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(key, sid);
    }
    setSessionId(sid);
    setMessages([]);
  }, [context]); // eslint-disable-line react-hooks/exhaustive-deps

  /* P0 prod: una sesión de chat guardada en localStorage puede pertenecer a
     OTRA cuenta (403 not_your_session) — antes eso rompía el asistente para
     siempre en ese navegador. Ahora se regenera la sesión y se reintenta. */
  const resetSession = () => {
    const sid = newSessionId();
    localStorage.setItem(`aos_chat_${context}`, sid);
    setSessionId(sid);
    return sid;
  };

  const authHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /* restore history when the assistant tab opens — never clobber an in-flight send */
  useEffect(() => {
    if (!open || tab !== "assistant" || !sessionId) return;
    fetch(`${BACKEND_URL}/api/ai/chat/${sessionId}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages((m) => (m.length ? m : d.messages || [])))
      .catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 250);
  }, [open, tab, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* C4: usuarios logueados abren el panel desde los accesos fijos (sidebar /
     barras superiores) — sin botón flotante que tape la navegación. */
  useEffect(() => {
    const onOpen = () => setOpen((v) => !v);
    window.addEventListener(OPEN_ACTIVITY_CENTER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ACTIVITY_CENTER_EVENT, onOpen);
  }, []);

  if (hidden) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setError(null);
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const doFetch = (sid) =>
        fetch(`${BACKEND_URL}/api/ai/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            session_id: sid,
            message: msg,
            context,
            event_slug: eventSlug,
            locale: i18n.resolvedLanguage,
          }),
        });
      let res = await doFetch(sessionId);
      if (res.status === 403) {
        // Session bound to another account — start fresh and retry once.
        res = await doFetch(resetSession());
      }
      if (res.status === 429) {
        const d = await res.json().catch(() => null);
        setMessages((m) => m.slice(0, -2));
        setError(d?.detail?.code === "daily_cap" ? t("ai.chatDaily") : t("ai.chatRateLimited"));
        return;
      }
      if (!res.ok || !res.body) throw new Error("chat_failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5));
          } catch {
            continue;
          }
          if (payload.delta) {
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              if (!last || last.role !== "assistant") return m;
              next[next.length - 1] = { ...last, content: last.content + payload.delta };
              return next;
            });
          } else if (payload.error) {
            throw new Error(payload.error);
          }
        }
      }
    } catch {
      setMessages((m) => (m[m.length - 1]?.role === "assistant" && m[m.length - 1].content === "" ? m.slice(0, -2) : m));
      setError(t("ai.chatError"));
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [1, 2, 3].map((i) => t(`ai.chatS_${context}_${i}`));

  return (
    <>
      {/* ---------- sheet ---------- */}
      <div
        role="dialog"
        aria-label={t("ai.centerTitle")}
        data-testid="ai-chat-panel"
        className={`fixed bottom-24 right-4 sm:right-6 z-[70] w-[min(92vw,380px)] flex flex-col overflow-hidden rounded-3xl border border-black/8 bg-white/95 backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.18)] transition-all duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        style={{ height: "min(65vh, 560px)" }}
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-black/5 shrink-0">
          <div className="flex items-center bg-black/5 rounded-full p-0.5" role="tablist" aria-label={t("ai.centerTitle")}>
            {[
              { key: "activity", icon: Activity, label: t("ai.tabActivity") },
              { key: "assistant", icon: Sparkles, label: t("ai.tabAssistant") },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                data-testid={`activity-center-tab-${key}`}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full text-xs font-semibold transition-colors duration-200 ${
                  tab === key ? "bg-white text-[#0A0A0A] shadow-sm" : "text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t("ai.chatClose")}
            data-testid="ai-chat-close"
            className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center transition-colors duration-200"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {tab === "activity" && <ActivityFeed open={open} />}

        {tab === "assistant" && (
        <>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" data-testid="ai-chat-messages">
          <div className="rounded-2xl rounded-tl-md bg-[#F5F5F4] px-4 py-3 text-sm text-[#0A0A0A] leading-relaxed max-w-[85%]">
            {t(`ai.chatGreeting_${context}`)}
          </div>
          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} data-testid={`ai-chat-message-${i}`} className="self-end rounded-2xl rounded-tr-md bg-[#0A0A0A] text-white px-4 py-3 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap">
                {m.content}
              </div>
            ) : (
              <div key={i} data-testid={`ai-chat-message-${i}`} className="rounded-2xl rounded-tl-md bg-[#F5F5F4] px-4 py-3 text-sm text-[#0A0A0A] leading-relaxed max-w-[85%] whitespace-pre-wrap">
                {m.content || (
                  <span className="inline-flex gap-1 items-center py-1" aria-label="…">
                    {[0, 150, 300].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 rounded-full bg-black/25 animate-pulse" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </span>
                )}
              </div>
            )
          )}
          {messages.length === 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`ai-chat-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="self-start rounded-full border border-black/10 px-4 py-2 text-xs text-[#0A0A0A]/70 hover:border-black/30 hover:text-[#0A0A0A] transition-colors duration-200 text-left"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {error && (
            <p role="alert" data-testid="ai-chat-error" className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="px-4 pb-2 pt-1 shrink-0"
        >
          <div className="flex items-center gap-2 rounded-full border border-black/10 bg-white pl-4 pr-1.5 py-1.5 focus-within:border-black/30 transition-colors duration-200">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("ai.chatPlaceholder")}
              aria-label={t("ai.chatPlaceholder")}
              data-testid="ai-chat-input"
              maxLength={1200}
              className="flex-1 bg-transparent text-sm text-[#0A0A0A] placeholder:text-black/30 outline-none min-w-0"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              aria-label={t("ai.chatSend")}
              data-testid="ai-chat-send"
              className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center disabled:opacity-25 transition-opacity duration-200 shrink-0"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ArrowUp className="w-4 h-4" aria-hidden="true" />}
            </button>
          </div>
        </form>
        <p className="px-5 pb-3 text-[10px] text-[#0A0A0A]/35 text-center shrink-0">{t("ai.chatDisclaimer")}</p>
        </>
        )}
      </div>

      {/* ---------- launcher (solo NO logueados: para usuarios con sesión el
           acceso vive en la navegación fija y nunca tapa contenido — C4) ---------- */}
      {user === null && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("ai.chatClose") : t("ai.centerTitle")}
          aria-expanded={open}
          data-testid="ai-chat-launcher"
          className="fixed bottom-5 right-4 sm:right-6 z-[70] w-12 h-12 flex items-center justify-center rounded-full bg-[#0A0A0A] text-white shadow-[0_12px_32px_rgba(0,0,0,0.28)] hover:scale-105 active:scale-95 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
        >
          {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Sparkles className="w-5 h-5" aria-hidden="true" />}
          {hasNews && (
            <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3" data-testid="activity-center-badge" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2F7A5D] opacity-60" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#2F7A5D] border-2 border-[#0A0A0A]" />
            </span>
          )}
        </button>
      )}
    </>
  );
};
